import { useEffect, useRef, useState } from 'react';
import type { StepProps } from '../engine/context.ts';
import { TitleBlock } from '../ui/TitleBlock.tsx';
import { Icon } from '../ui/Icon.tsx';
import { asset } from '../engine/assets.ts';
import { GRAD, INK, LINE, MUTED, PAPER, SHADOW_RAISED } from '../ui/theme.ts';

/** Pointer-captured divider over a 350×440 crop. `divider_drag` fires once, with the final pct. */
export function BeforeAfter({ step, content, ctx }: StepProps) {
  const [pct, setPct] = useState(step.startPct ?? 50);
  const dragging = useRef(false);
  const moved = useRef(false);
  const pctRef = useRef(pct);

  useEffect(() => {
    pctRef.current = pct;
  }, [pct]);

  useEffect(() => {
    ctx.setCta({ label: content.cta ?? ctx.shared.ui?.continue ?? 'Continue', onPress: ctx.next });
    return () => {
      if (moved.current) ctx.event('divider_drag', { pct: Math.round(pctRef.current) });
    };
  }, [step.id]);

  const at = (e: React.PointerEvent) => {
    const r = e.currentTarget.getBoundingClientRect();
    return Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100));
  };

  return (
    <>
      <TitleBlock label={content.label} title={content.title} highlight={content.highlight} subtitle={content.subtitle} />

      <div
        onPointerDown={(e) => {
          (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
          dragging.current = true;
          moved.current = true;
          setPct(at(e));
        }}
        onPointerMove={(e) => dragging.current && setPct(at(e))}
        onPointerUp={() => (dragging.current = false)}
        onPointerCancel={() => (dragging.current = false)}
        style={{
          flex: 'none',
          position: 'relative',
          width: '100%',
          height: 'min(440px, 113vw)',
          borderRadius: 24,
          overflow: 'hidden',
          background: INK,
          touchAction: 'none',
          cursor: 'ew-resize',
          boxShadow: SHADOW_RAISED,
        }}
      >
        <div style={{ position: 'absolute', inset: 0, backgroundSize: 'cover', backgroundPosition: 'center', backgroundImage: `url(${asset(step.after)})` }} />
        <div style={{ position: 'absolute', inset: 0, width: `${pct}%`, overflow: 'hidden' }}>
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              height: '100%',
              width: `${10000 / Math.max(1, pct)}%`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundImage: `url(${asset(step.before)})`,
            }}
          />
        </div>
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: `${pct}%`, width: 3, background: '#fff', transform: 'translateX(-50%)' }} />
        <span
          role="slider"
          tabIndex={0}
          aria-label="Before and after"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(pct)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') setPct((p) => Math.max(0, p - 4));
            if (e.key === 'ArrowRight') setPct((p) => Math.min(100, p + 4));
          }}
          style={{
            position: 'absolute',
            top: '50%',
            left: `${pct}%`,
            transform: 'translate(-50%,-50%)',
            width: 44,
            height: 44,
            borderRadius: '50%',
            background: PAPER,
            boxShadow: '0 14px 36px rgba(31,27,45,.2)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="arrows-out-line-horizontal" size={22} />
        </span>
        <span style={{ position: 'absolute', left: 12, top: 12, padding: '5px 10px', borderRadius: 999, background: 'rgba(255,255,255,.92)', fontSize: 12, fontWeight: 800 }}>
          {content.beforeLabel ?? 'Before'}
        </span>
        <span style={{ position: 'absolute', right: 12, top: 12, padding: '5px 10px', borderRadius: 999, background: GRAD, color: '#fff', fontSize: 12, fontWeight: 800 }}>
          {content.afterLabel ?? 'After'}
        </span>
      </div>

      {content.stat ? (
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ padding: '10px 14px', borderRadius: 999, background: PAPER, border: `2px solid ${LINE}`, fontWeight: 800, fontSize: 14 }}>
            {content.stat.label} {content.stat.before}
          </span>
          <Icon name="arrow-right" size={18} color={MUTED} />
          <span style={{ padding: '10px 14px', borderRadius: 999, background: GRAD, color: '#fff', fontWeight: 800, fontSize: 14 }}>{content.stat.after}</span>
        </div>
      ) : null}
    </>
  );
}
